import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useUserPermissions } from 'auth/hooks';
import { ActionButton, Confirmation } from 'core/components';
import { AccessPermissions, ActionButtonType, NotificationPosition } from 'core/enums';
import { IPricingProfile } from 'pricing/types';
import { VendorQuoteAttachToType } from 'quotes/enums';
import {
  ICustomerQuote,
  IImportVendorQuoteForm,
  IQuoteQuoteAssociationForm,
  IVendorQuote,
} from 'quotes/types';
import { Variant } from 'theme/enums';

import { ImportVendorQuoteForm, TImportVendorQuoteHiddenFields } from './ImportVendorQuoteForm';
import { useImportVendorQuote } from './useImportVendorQuote';

// Stable identity so an omitted `defaultValues` prop doesn't churn the memoized form defaults.
const EMPTY_IMPORT_DEFAULTS: Partial<IImportVendorQuoteForm> = {};

type IProps = {
  quote?: IVendorQuote | ICustomerQuote;
  pricingProfile?: IPricingProfile;
  onImportComplete?: (quoteId: number) => void;
  refetchQueriesKeys?: string[];
  isUpdatingCurrentQuote?: boolean;
  withoutConfirmation?: boolean;
  notificationPosition?: NotificationPosition;
  disabled?: boolean;
  defaultValues?: Partial<IImportVendorQuoteForm>;
  hiddenFields?: TImportVendorQuoteHiddenFields;
};

export const ImportVendorQuoteButton = ({
  quote,
  pricingProfile,
  onImportComplete,
  refetchQueriesKeys,
  isUpdatingCurrentQuote,
  withoutConfirmation,
  notificationPosition,
  disabled = false,
  defaultValues: customDefaultValues = EMPTY_IMPORT_DEFAULTS,
  hiddenFields: customHiddenFields,
}: IProps) => {
  const [quoteQuoteAssociation, setQuoteQuoteAssociation] = useState<IQuoteQuoteAssociationForm>();
  const { t } = useTranslation();
  const { hasPermission } = useUserPermissions();

  const isVendorQuote = quote && 'customerProjectName' in quote;

  const {
    importedQuoteData,
    extractedImportId,
    vendorContact,
    vendorMapping,
    vendorQuoteNumber,
    vendorQuoteRevision,
    isFinalizeFailed,
    isOpenImportVendorQuoteForm,
    onCloseImportVendorQuoteForm,
    onImportVendorQuote,
    onUploadVendorQuote,
    confirmationOption,
    resolveVendorQuoteDuplicate,
    startVendorQuoteImport,
  } = useImportVendorQuote({
    currentProjectId: quote?.vendorProjectId,
    currentJobId: quote?.bomId,
    currentQuoteId: quote?.id,
    pricingProfile,
    isUpdatingCurrentQuote,
    notificationPosition,
    setQuoteQuoteAssociation,
    refetchQueriesKeys,
    onImportComplete: () =>
      onImportComplete && onImportComplete(quoteQuoteAssociation?.vendorQuotes?.[0]),
    withoutConfirmation,
  });

  const defaultValues = useMemo(
    () => ({
      ...(isVendorQuote && {
        project: {
          id: quote.customerProjectId,
          name: quote.customerProjectName,
        },
        contact: {
          id: quote.vendorCorporateUserId,
          name: quote.vendorCorporateUserName,
          companyName: quote.vendorContactGroupName,
        },
      }),
      attachTo: pricingProfile
        ? VendorQuoteAttachToType.COSTING_PROFILE
        : VendorQuoteAttachToType.JOB,
      ...customDefaultValues,
    }),
    [customDefaultValues, isVendorQuote, quote, pricingProfile]
  );

  const hiddenFields = useMemo(() => {
    return {
      ...(isVendorQuote || pricingProfile ? { attachTo: true } : null),
      ...customHiddenFields,
    };
  }, [customHiddenFields, isVendorQuote, pricingProfile]);

  // The create-import endpoint rejects users without the resource, so offer no upload control at
  // all rather than a button that always 403s. The form goes with it — it is only reachable
  // through the upload.
  if (!hasPermission(AccessPermissions.IMPORT_VENDOR_QUOTE)) {
    return null;
  }

  return (
    <>
      <ActionButton
        action={{
          type: ActionButtonType.BUTTON_FILE_UPLOAD,
          label: t('buttonLabelImportVendorQuote'),
          variant: Variant.SOLID,
          onUpload: (file: File) => onUploadVendorQuote(file, quote?.id),
          disabled,
        }}
      />
      <ImportVendorQuoteForm
        disabled={isVendorQuote}
        quote={isVendorQuote ? quote : undefined}
        importedQuoteData={importedQuoteData}
        extractedImportId={extractedImportId}
        vendorContact={vendorContact}
        vendorMapping={vendorMapping}
        vendorQuoteNumber={vendorQuoteNumber}
        vendorQuoteRevision={vendorQuoteRevision}
        isFinalizeFailed={isFinalizeFailed}
        isOpen={isOpenImportVendorQuoteForm}
        onClose={onCloseImportVendorQuoteForm}
        onImport={onImportVendorQuote}
        onResolveDuplicate={resolveVendorQuoteDuplicate}
        onStartImport={startVendorQuoteImport}
        hiddenFields={hiddenFields}
        defaultValues={defaultValues}
      />
      {confirmationOption && <Confirmation isOpen={true} {...confirmationOption} />}
    </>
  );
};
